import { useState, useEffect, useMemo, useRef } from 'react'
import { useUnit } from '@/contexts/UnitContext'
import { useTranslation } from '@/contexts/TranslationContext'
import { useBilling } from '@/contexts/BillingContext'
import { useToast } from '@/hooks/useToast'
import { matchesSearch } from '@/lib/search'
import { type FinancialEntry, type EntryType, type VirtualEntry, fetchVirtualEntries, generateAccountsPDF, generateAccountsText, getPartyById } from '@/lib/services/billing'
import EntryModal from './EntryModal'
import EntryActions from './EntryActions'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function toDateKey(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function buildCalendarDays(year: number, month: number): (Date | null)[] {
  const firstDay = new Date(year, month, 1)
  const lastDay  = new Date(year, month + 1, 0)
  // Sunday = 0 offset; getDay() returns 0=Sun, no shift needed
  const startOffset = firstDay.getDay()
  const days: (Date | null)[] = Array(startOffset).fill(null)
  for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(year, month, d))
  while (days.length % 7 !== 0) days.push(null)
  return days
}

/** Returns the set of date-keys between anchor and current (inclusive) based on the calendar layout. */
function computeRange(anchor: string, current: string, calendarDays: (Date | null)[]): Set<string> {
  const allKeys = calendarDays
    .filter((d): d is Date => d !== null)
    .map(d => toDateKey(d))
  const anchorIdx  = allKeys.indexOf(anchor)
  const currentIdx = allKeys.indexOf(current)
  if (anchorIdx === -1 || currentIdx === -1) return new Set([anchor])
  const [start, end] = anchorIdx <= currentIdx ? [anchorIdx, currentIdx] : [currentIdx, anchorIdx]
  return new Set(allKeys.slice(start, end + 1))
}

// ─── Main component ───────────────────────────────────────────────────────────

export function BillingDashboard() {
  const { currentUnit, userRole } = useUnit()
  const { t, locale }   = useTranslation()
  const { entries, loading, fetchEntries, invalidateEntries } = useBilling()
  const isMember = userRole === 'viewer'

  const LOCALE_CURRENCY: Record<string, string> = { 'pt-BR': 'BRL', 'en-US': 'USD', 'es-ES': 'EUR' }
  const currency = LOCALE_CURRENCY[locale] ?? 'BRL'

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount)

  const formatDateLabel = (dateStr: string) => {
    const [y, m, d] = dateStr.split('-').map(Number)
    return new Date(y, m - 1, d).toLocaleDateString(locale, {
      weekday: 'long', day: '2-digit', month: 'long',
    })
  }

function formatDate(dateStr: string, locale: string) {
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString(locale)
}

  // Locale-aware month names (long)
  const MONTH_NAMES = useMemo(() =>
    Array.from({ length: 12 }, (_, i) =>
      new Date(2000, i, 1).toLocaleDateString(locale, { month: 'long' })
        .replace(/^\w/, c => c.toUpperCase())
    ), [locale])

  // Locale-aware week day headers (Sun-first short)
  const WEEK_DAYS = useMemo(() => {
    // Generate Sun..Sat labels in locale
    const base = new Date(2024, 0, 7) // 2024-01-07 = Sunday
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(base)
      d.setDate(base.getDate() + i)
      return d.toLocaleDateString(locale, { weekday: 'short' })
        .replace('.', '').slice(0, 3)
        .replace(/^\w/, c => c.toUpperCase())
    })
  }, [locale])

  const db = (key: string) => t(`billing.dashboard.${key}`)
  const te = (key: string, params?: Record<string, string>) => t(`billing.entries.${key}`, params)

  const { error: showError, ToastContainer } = useToast()
  const [copiedAmountIds, setCopiedAmountIds] = useState<string[]>([])
  const [copiedPixIds, setCopiedPixIds] = useState<string[]>([])


  const markCopiedAmount = (id: string) => {
    setCopiedAmountIds(prev => prev.includes(id) ? prev : [...prev, id])
    setTimeout(() => setCopiedAmountIds(prev => prev.filter(x => x !== id)), 3000)
  }

  const markCopiedPix = (id: string) => {
    setCopiedPixIds(prev => prev.includes(id) ? prev : [...prev, id])
    setTimeout(() => setCopiedPixIds(prev => prev.filter(x => x !== id)), 3000)
  }

  const handleCopyPix = async (pixKey: string | undefined, entryId?: string) => {
    if (!pixKey) return
    try {
      await navigator.clipboard.writeText(pixKey)
      if (entryId) markCopiedPix(entryId)
    } catch (err) {
      console.error('copy pix error', err)
      showError('Erro ao copiar')
    }
  }

  // Replicate now handled by EntryActions; page-level handler removed.

  const handleCopyAmount = async (amount: number, entryId?: string) => {
    try {
      await navigator.clipboard.writeText(formatCurrency(amount))
      if (entryId) markCopiedAmount(entryId)
    } catch (err) {
      console.error('copy amount error', err)
      showError('Erro ao copiar')
    }
  }

  // ─── Share functionality ──────────────────────────────────────────────────

  async function handleShareText(): Promise<void> {
    // Limpar timer anterior se existir
    if (shareMessageTimerRef.current) {
      window.clearTimeout(shareMessageTimerRef.current)
      shareMessageTimerRef.current = null
    }

    if (selectedEntries.length === 0) {
      setShareMessage({ type: 'error', text: db('share.empty') })
      shareMessageTimerRef.current = window.setTimeout(() => setShareMessage(null), 3000)
      return
    }

    try {
      console.log('[BillingDashboard] Iniciando geração de texto...')
      // Gerar texto compartilhável
      const text = await generateAccountsText(
        selectedEntries,
        locale,
        currency,
        startOfMonth,
        endOfMonth,
      )
      console.log('[BillingDashboard] Texto gerado com sucesso!')

      // Compartilhar via Web Share API
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Contas a Receber/Pagar',
            text: text,
          })
          console.log('[BillingDashboard] Texto compartilhado com sucesso!')
          setShareMessage({ type: 'success', text: 'Compartilhado com sucesso!' })
        } catch (shareErr) {
          if (shareErr instanceof Error && shareErr.name === 'NotAllowedError') {
            console.log('[BillingDashboard] Compartilhamento cancelado')
            return
          }
          // Fallback: copiar para clipboard
          navigator.clipboard.writeText(text)
          setShareMessage({ type: 'success', text: 'Texto copiado para a área de transferência!' })
        }
      } else {
        // Fallback: copiar para clipboard
        navigator.clipboard.writeText(text)
        setShareMessage({ type: 'success', text: 'Texto copiado para a área de transferência!' })
      }
      shareMessageTimerRef.current = window.setTimeout(() => setShareMessage(null), 3000)
    } catch (err) {
      console.error('[BillingDashboard] Share error:', err)
      setShareMessage({ type: 'error', text: 'Erro ao compartilhar' })
      shareMessageTimerRef.current = window.setTimeout(() => setShareMessage(null), 3000)
    }
  }

  async function handleDownloadPDF(): Promise<void> {
    // Limpar timer anterior se existir
    if (shareMessageTimerRef.current) {
      window.clearTimeout(shareMessageTimerRef.current)
      shareMessageTimerRef.current = null
    }

    if (selectedEntries.length === 0) {
      setShareMessage({ type: 'error', text: db('share.empty') })
      shareMessageTimerRef.current = window.setTimeout(() => setShareMessage(null), 3000)
      return
    }

    try {
      console.log('[BillingDashboard] Iniciando download de PDF...')
      // Gerar e fazer download do PDF
      await generateAccountsPDF(
        selectedEntries,
        locale,
        currency,
        startOfMonth,
        endOfMonth,
      )
      console.log('[BillingDashboard] PDF baixado com sucesso!')
      setShareMessage({ type: 'success', text: 'PDF baixado com sucesso!' })
      shareMessageTimerRef.current = window.setTimeout(() => setShareMessage(null), 3000)
    } catch (err) {
      console.error('[BillingDashboard] Download error:', err)
      setShareMessage({ type: 'error', text: 'Erro ao gerar PDF' })
      shareMessageTimerRef.current = window.setTimeout(() => setShareMessage(null), 3000)
    }
  }

  function StatusBadge({ entry }: { entry: FinancialEntry }) {
    const today = toDateKey(new Date())
    const overdue = entry.status === 'open' && entry.due_date < today
    const isReceivable = entry.entry_type === 'receivable'
    const styles: Record<string, Record<string, string>> = {
      receivable: {
        overdue:  'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
        paid:     'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
        open:     'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
        canceled: 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
      },
      payable: {
        overdue:  'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
        paid:     'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
        open:     'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
        canceled: 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
      }
    }
    const key = overdue ? 'overdue' : entry.status
    
    // Customized labels for dashboard: "Recebido" for receivable paid, "Pago" for payable paid
    let label = db(`status.${key}`)
    if (entry.status === 'paid') {
      label = isReceivable ? db('status.received') : db('status.paid')
    }
    
    const typeStyles = styles[isReceivable ? 'receivable' : 'payable']
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${typeStyles[key]}`}>
        {(entry as any)?.notify_on_due && (
          <svg className="h-3 w-3 text-amber-600 dark:text-amber-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        )}
        {entry.receipt_url && (
          <svg className="h-3 w-3 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
          </svg>
        )}
        {label}
      </span>
    )
  }

  const [viewDate, setViewDate] = useState(() => new Date())
  const [searchQuery, setSearchQuery] = useState('')
  const [showSearchPopover, setShowSearchPopover] = useState(false)
  const searchHintRef = useRef<HTMLDivElement | null>(null)
  const searchBtnRef = useRef<HTMLButtonElement | null>(null)
  const [selectedKeys, setSelectedKeys]   = useState<Set<string>>(() => new Set([toDateKey(new Date())]))
  const isDraggingRef                     = useRef(false)
  const dragAnchorRef                     = useRef<string | null>(null)
  // Long-press state for mobile touch drag selection
  const longPressTimerRef = useRef<number | null>(null)
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null)
  const isDragReadyRef = useRef(false)
  // Prevents synthetic mousedown (fired by browser after touch) from toggling selection
  const touchHandledRef = useRef(false)
  const [fabOpen, setFabOpen]             = useState(false)
  const [modalType, setModalType]     = useState<EntryType | null>(null)
  const LONG_PRESS_MS = 500
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const check = () => setIsMobile(typeof window !== 'undefined' ? window.innerWidth < 768 : false)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  // Close search popover when clicking outside
  useEffect(() => {
    if (!showSearchPopover) return
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node
      if (searchHintRef.current?.contains(t) || searchBtnRef.current?.contains(t)) return
      setShowSearchPopover(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [showSearchPopover])
  // Postpone and replicate occurrences handled by EntryActions; page-level handlers removed.
  const [shareMessage, setShareMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const desktopRef = useRef<HTMLDivElement>(null)
  const mobileRef  = useRef<HTMLDivElement>(null)
  const shareMessageTimerRef = useRef<number | null>(null)

  const year  = viewDate.getFullYear()
  const month = viewDate.getMonth()

  const [startOfMonth, endOfMonth] = useMemo(() => {
    const y = String(year)
    const m = String(month + 1).padStart(2, '0')
    const last = new Date(year, month + 1, 0).getDate()
    return [`${y}-${m}-01`, `${y}-${m}-${String(last).padStart(2, '0')}`]
  }, [year, month])

  // Fetch entries from context when unit or date range changes
  useEffect(() => {
    if (!currentUnit?.id) return
    fetchEntries(currentUnit.id, startOfMonth, endOfMonth)
  }, [currentUnit?.id, startOfMonth, endOfMonth, fetchEntries])

  // Fetch virtual entries from backend VIEW and merge with real ones
  type AnyEntry = FinancialEntry | VirtualEntry
  const [allEntries, setAllEntries] = useState<AnyEntry[]>([])
  useEffect(() => {
    if (!currentUnit?.id || loading) return
    let cancelled = false
    ;(async () => {
      const virtuals = await fetchVirtualEntries(currentUnit.id, startOfMonth, endOfMonth)
      if (cancelled) return
      setAllEntries([...entries, ...virtuals].sort((a, b) => a.due_date.localeCompare(b.due_date)))
    })()
    return () => { cancelled = true }
  }, [entries, currentUnit?.id, startOfMonth, endOfMonth, loading])

  // Filter entries by search query (description, party, amount with ranges/approx/comparisons)
  const filteredEntries = useMemo(() => {
    const q = searchQuery.trim()
    if (!q) return allEntries

    const parseNum = (s: string) => {
      if (!s) return NaN
      const cleaned = s.replace(/[^0-9.,-]/g, '').replace(/\./g, '').replace(',', '.')
      return parseFloat(cleaned)
    }

    return allEntries.filter(e => {
      const inDesc = matchesSearch(e.description, q)
      const inParty = matchesSearch((e as any).party_name_snapshot ?? '', q)
      let inAmount = false

      const rangeMatch = q.match(/^(\d[0-9.,]*)\s*-\s*(\d[0-9.,]*)$/)
      if (rangeMatch) {
        const min = parseNum(rangeMatch[1])
        const max = parseNum(rangeMatch[2])
        if (!isNaN(min) && !isNaN(max)) inAmount = e.amount >= Math.min(min, max) && e.amount <= Math.max(min, max)
      }

      const approxMatch = q.match(/^~\s*(\d[0-9.,]*)$/)
      if (!inAmount && approxMatch) {
        const v = parseNum(approxMatch[1])
        if (!isNaN(v)) {
          const tol = Math.max(1, Math.abs(v) * 0.05)
          inAmount = Math.abs(e.amount - v) <= tol
        }
      }

      const compMatch = q.match(/^([<>]=?)\s*(\d[0-9.,]*)$/)
      if (!inAmount && compMatch) {
        const op = compMatch[1]
        const v = parseNum(compMatch[2])
        if (!isNaN(v)) {
          if (op === '>') inAmount = e.amount > v
          else if (op === '>=') inAmount = e.amount >= v
          else if (op === '<') inAmount = e.amount < v
          else if (op === '<=') inAmount = e.amount <= v
        }
      }

      if (!inAmount && /[0-9]/.test(q)) {
        const parsed = parseNum(q)
        if (!isNaN(parsed)) {
          const tol = Math.max(0.01, Math.abs(parsed) * 0.05)
          inAmount = e.amount === parsed || Math.abs(e.amount - parsed) <= tol
        }
      }

      return inDesc || inParty || inAmount
    })
  }, [allEntries, searchQuery])

  // If some entries lack a current pix snapshot but have a party_id, fetch parties and merge pix_key
  useEffect(() => {
    if (!currentUnit?.id || allEntries.length === 0) return
    let cancelled = false
    ;(async () => {
      const missing = Array.from(new Set(allEntries
        .filter(e => e.party_id && !e.party_pix_key_snapshot)
        .map(e => e.party_id as string)
      ))
      if (missing.length === 0) return
      try {
        console.log('[BillingDashboard] fetching missing pix for party ids:', missing)
        const pairs = await Promise.all(missing.map(id => getPartyById(id).catch(err => {
          console.warn('[BillingDashboard] getPartyById failed for', id, err)
          return null
        })))
        if (cancelled) return
        console.log('[BillingDashboard] fetched parties:', pairs)
        const pixMap = new Map<string, string | null>()
        pairs.forEach(p => { if (p) pixMap.set(p.id, p.pix_key ?? null) })
        if (pixMap.size === 0) return
        setAllEntries(prev => prev.map(entry => {
          if (entry.party_id && !entry.party_pix_key_snapshot && pixMap.has(entry.party_id)) {
            return { ...entry, party_pix_key_snapshot: pixMap.get(entry.party_id) ?? null }
          }
          return entry
        }))
      } catch (err) {
        console.warn('[BillingDashboard] failed to fetch missing party pix keys', err)
      }
    })()
    return () => { cancelled = true }
  }, [allEntries, currentUnit?.id])

  // Close FAB when clicking outside
  useEffect(() => {
    if (!fabOpen) return
    function handleClick(e: MouseEvent) {
      const t = e.target as Node
      const outsideDesktop = !desktopRef.current || !desktopRef.current.contains(t)
      const outsideMobile  = !mobileRef.current  || !mobileRef.current.contains(t)
      if (outsideDesktop && outsideMobile) setFabOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [fabOpen])

  // Cleanup share message timer on unmount
  useEffect(() => {
    return () => {
      if (shareMessageTimerRef.current) {
        window.clearTimeout(shareMessageTimerRef.current)
        shareMessageTimerRef.current = null
      }
    }
  }, [])

  const entryMap = useMemo(() => {
    const map: Record<string, AnyEntry[]> = {}
    for (const e of filteredEntries) {
      if (!map[e.due_date]) map[e.due_date] = []
      map[e.due_date].push(e)
    }
    return map
  }, [filteredEntries])

  const isVirtualEntry = (entry: AnyEntry): entry is VirtualEntry => ('isVirtual' in entry) && (entry as any).isVirtual === true

  // Virtual entry materialization handled by EntryActions; removed from page.

  // Receivable totals (real + virtual entries) - filtered by search query
  const openReceivable = useMemo(
    () => filteredEntries.filter(e => e.entry_type === 'receivable' && e.status === 'open').reduce((s, e) => s + e.amount, 0),
    [filteredEntries],
  )
  const paidReceivable = useMemo(
    () => filteredEntries.filter(e => e.entry_type === 'receivable' && e.status === 'paid').reduce((s, e) => s + e.amount, 0),
    [filteredEntries],
  )
  const totalReceivable = openReceivable + paidReceivable

  // Payable totals (real + virtual entries) - filtered by search query
  const openPayable = useMemo(
    () => filteredEntries.filter(e => e.entry_type === 'payable' && e.status === 'open').reduce((s, e) => s + e.amount, 0),
    [filteredEntries],
  )
  const paidPayable = useMemo(
    () => filteredEntries.filter(e => e.entry_type === 'payable' && e.status === 'paid').reduce((s, e) => s + e.amount, 0),
    [filteredEntries],
  )
  const totalPayable = openPayable + paidPayable

  const calendarDays    = useMemo(() => buildCalendarDays(year, month), [year, month])
  const today           = toDateKey(new Date())
  const selectedEntries = useMemo(() => {
    if (selectedKeys.size === 0) return []
    const result: AnyEntry[] = []
    for (const k of [...selectedKeys].sort()) {
      if (entryMap[k]) result.push(...entryMap[k])
    }
    // Sort: receivable first, then payable
    return result.sort((a, b) => {
      if (a.entry_type === b.entry_type) return 0
      return a.entry_type === 'receivable' ? -1 : 1
    })
  }, [selectedKeys, entryMap])

  // Ensure today is selected when viewing current month
  useEffect(() => {
    const now = new Date()
    if (viewDate.getFullYear() === now.getFullYear() && viewDate.getMonth() === now.getMonth()) {
      setSelectedKeys(prev => new Set([...prev, today]))
    }
  }, [today, viewDate])

  // End drag on mouse release anywhere in document
  useEffect(() => {
    const handleMouseUp = () => { isDraggingRef.current = false }
    document.addEventListener('mouseup', handleMouseUp)
    return () => document.removeEventListener('mouseup', handleMouseUp)
  }, [])

  // Touch drag for multi-day calendar selection (mobile)
  useEffect(() => {
    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0]
      if (!touch || !touchStartPosRef.current) return

      const dx = touch.clientX - touchStartPosRef.current.x
      const dy = touch.clientY - touchStartPosRef.current.y
      const distSq = dx * dx + dy * dy
      const THRESHOLD_DRAG = 15 // px threshold to start drag

      // If long-press is ready and user moved enough, activate drag mode
      if (isDragReadyRef.current && !isDraggingRef.current && distSq > THRESHOLD_DRAG * THRESHOLD_DRAG) {
        isDraggingRef.current = true
      }

      // Only prevent default and handle drag if we're actively dragging
      if (!isDraggingRef.current || !dragAnchorRef.current) return

      e.preventDefault()
      const el = document.elementFromPoint(touch.clientX, touch.clientY)
      const btn = el?.closest<HTMLElement>('[data-datekey]')
      if (btn?.dataset?.datekey) {
        setSelectedKeys(computeRange(dragAnchorRef.current, btn.dataset.datekey, calendarDays))
      }
    }

    document.addEventListener('touchmove', handleTouchMove, { passive: false })
    return () => {
      document.removeEventListener('touchmove', handleTouchMove)
    }
  }, [calendarDays])

  const prevMonth = () => { setSelectedKeys(new Set()); setViewDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1)) }
  const nextMonth = () => { setSelectedKeys(new Set()); setViewDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1)) }
  const goToday   = () => { setViewDate(new Date()); setSelectedKeys(new Set([toDateKey(new Date())])) }

  // Helper: determines if a day is the first in a multi-day range
  const isRangeStart = (dayKey: string): boolean => {
    if (selectedKeys.size <= 1) return false
    const sorted = [...selectedKeys].sort()
    return dayKey === sorted[0]
  }

  // Helper: determines if a day is the last in a multi-day range
  const isRangeEnd = (dayKey: string): boolean => {
    if (selectedKeys.size <= 1) return false
    const sorted = [...selectedKeys].sort()
    return dayKey === sorted[sorted.length - 1]
  }

  // Helper: formats the date range for display (e.g., "09/04/2026 a 15/04/2026")
  const getDateRangeLabel = (): string => {
    if (selectedKeys.size === 0) return ''
    if (selectedKeys.size === 1) return ''
    const sorted = [...selectedKeys].sort()
    const firstDate = new Date(parseInt(sorted[0].split('-')[0]), parseInt(sorted[0].split('-')[1]) - 1, parseInt(sorted[0].split('-')[2]))
    const lastDate = new Date(parseInt(sorted[sorted.length - 1].split('-')[0]), parseInt(sorted[sorted.length - 1].split('-')[1]) - 1, parseInt(sorted[sorted.length - 1].split('-')[2]))
    const first = firstDate.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' })
    const last = lastDate.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' })
    return `${first} - ${last}`
  }

  const handleNewEntry = (type: EntryType) => {
    setFabOpen(false)
    setModalType(type)
  }

  // Default due date for creating a new entry: first selected day or today
  const defaultDueDateForNewEntry = useMemo(() => {
    if (selectedKeys.size === 0) return new Date().toISOString().split('T')[0]
    const sorted = [...selectedKeys].sort()
    return sorted[0]
  }, [selectedKeys])

  const handleModalSuccess = () => {
    // Refresh dashboard entries after a new entry is created
    if (!currentUnit?.id) return
    // Use the billing context fetch to refresh and keep caching consistent
    // Invalidate cached entries so BillingContext will refetch regardless of cache key
    try { invalidateEntries() } catch (e) { /* noop */ }
  }


  return (
    <>
    <div className="max-w-screen-2xl mx-auto px-4 lg:px-6 py-6 space-y-6">

      {/* ── Page header (sticky) ─────────────────────────────────────────── */}
      <div className="sticky top-0 z-40 bg-white/60 dark:bg-slate-900/60 backdrop-blur-sm -mx-4 lg:-mx-6 px-4 lg:px-6 py-4">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center justify-between gap-3">
              <h1 className="text-lg font-semibold text-slate-900 dark:text-white">{db('title')}</h1>
              {/* Mobile: search button with visual feedback */}
              <button
                ref={r => { if (r) searchBtnRef.current = r }}
                onClick={() => setShowSearchPopover(s => !s)}
                className="sm:hidden flex items-center justify-center w-9 h-9 rounded-lg bg-slate-100 hover:bg-slate-200 active:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 dark:active:bg-slate-500 text-slate-600 dark:text-slate-300 transition-colors"
                aria-label={te('filters.searchReceivable')}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15z" />
                </svg>
              </button>
            </div>
            <div className="hidden sm:flex items-center gap-2 relative" ref={desktopRef}>
              {/* Desktop: search button with label (styled like New Entry) */}
              <button
                ref={r => { if (r) searchBtnRef.current = r }}
                onClick={() => setShowSearchPopover(s => !s)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 active:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 dark:active:bg-slate-500 text-slate-700 dark:text-slate-200 text-sm font-semibold shadow-sm transition-colors"
                aria-label={te('filters.searchReceivable')}
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15z" />
                </svg>
                Buscar
              </button>
              {!isMember && (
                <>
                  <button
                    onClick={() => setFabOpen(o => !o)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-semibold shadow-sm transition-colors"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {db('newEntry')}
                  </button>
              {/* Search popover (anchored to search button) */}
              {showSearchPopover && (
                <div ref={searchHintRef} className="absolute right-0 top-full mt-3 w-[640px] bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden z-40 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{t('actions.search')}</span>
                    <button
                      onClick={() => setShowSearchPopover(false)}
                      className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:text-slate-200 dark:hover:bg-slate-700 transition-colors"
                      aria-label={t('actions.close')}
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') setShowSearchPopover(false) }}
                      placeholder={t('actions.search') + '...'}
                      autoFocus
                      className="w-full px-3 py-2 pr-8 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                        aria-label="Limpar"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-3">{te('filters.searchHintShort')}</p>
                </div>
              )}
                  {fabOpen && (
                    <div className="absolute right-0 top-full mt-1.5 w-52 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden z-30">
                      <button
                        onClick={() => handleNewEntry('receivable')}
                        className="flex items-center gap-3 w-full px-4 py-3 text-sm font-medium text-left hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
                      >
                        <span className="w-7 h-7 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-bold">↑</span>
                        <span className="text-slate-800 dark:text-slate-200">{db('newReceivable')}</span>
                      </button>
                      <div className="border-t border-slate-100 dark:border-slate-700" />
                      <button
                        onClick={() => handleNewEntry('payable')}
                        className="flex items-center gap-3 w-full px-4 py-3 text-sm font-medium text-left hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
                      >
                        <span className="w-7 h-7 rounded-full bg-rose-100 dark:bg-rose-900/40 flex items-center justify-center text-rose-600 dark:text-rose-400 font-bold">↓</span>
                        <span className="text-slate-800 dark:text-slate-200">{db('newPayable')}</span>
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Summary cards ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3">
          {/* A Receber */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
            <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide mb-2">{db('cards.receivable')}</p>
            <p className="text-lg sm:text-2xl font-bold text-emerald-600 dark:text-emerald-400 tabular-nums leading-none mb-3">
              {loading ? '—' : formatCurrency(totalReceivable)}
            </p>
            <div className="space-y-1 border-t border-slate-100 dark:border-slate-700 pt-2">
              <div className="flex justify-between text-xs">
                <span className="text-slate-500 dark:text-slate-400">{db('cards.open')}</span>
                <span className="font-medium text-amber-600 dark:text-amber-400 tabular-nums">{loading ? '—' : formatCurrency(openReceivable)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500 dark:text-slate-400">{db('cards.received')}</span>
                <span className="font-medium text-emerald-600 dark:text-emerald-400 tabular-nums">{loading ? '—' : formatCurrency(paidReceivable)}</span>
              </div>
            </div>
          </div>

          {/* A Pagar */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
            <p className="text-xs font-semibold text-rose-600 dark:text-rose-400 uppercase tracking-wide mb-2">{db('cards.payable')}</p>
            <p className="text-lg sm:text-2xl font-bold text-rose-600 dark:text-rose-400 tabular-nums leading-none mb-3">
              {loading ? '—' : formatCurrency(totalPayable)}
            </p>
            <div className="space-y-1 border-t border-slate-100 dark:border-slate-700 pt-2">
              <div className="flex justify-between text-xs">
                <span className="text-slate-500 dark:text-slate-400">{db('cards.open')}</span>
                <span className="font-medium text-amber-600 dark:text-amber-400 tabular-nums">{loading ? '—' : formatCurrency(openPayable)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500 dark:text-slate-400">{db('cards.paid')}</span>
                <span className="font-medium text-rose-600 dark:text-rose-400 tabular-nums">{loading ? '—' : formatCurrency(paidPayable)}</span>
              </div>
            </div>
          </div>
        </div>

      {/* ── Calendar + Day panel side-by-side on desktop ──────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Calendar */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">

          {/* Month navigation */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700">
            <button onClick={prevMonth} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="flex items-center gap-3">
              <span className="font-semibold text-slate-900 dark:text-white">{MONTH_NAMES[month]} {year}</span>
              <button onClick={goToday} className="text-xs px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors">
                {db('calendar.today')}
              </button>
            </div>

            <button onClick={nextMonth} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Week day headers */}
          <div className="grid grid-cols-7 border-b border-slate-200 dark:border-slate-700">
            {WEEK_DAYS.map(d => (
              <div key={d} className="py-2 text-center text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wide">
                {d}
              </div>
            ))}
          </div>

          {/* Days grid */}
          <div className="grid grid-cols-7">
            {calendarDays.map((day, i) => {
              if (!day) return (
                <div key={`empty-${i}`} className="h-14 border-b border-r border-slate-100 dark:border-slate-700/40 bg-slate-50/50 dark:bg-slate-900/20" />
              )

              const key            = toDateKey(day)
              const dayEntries     = entryMap[key] || []
              const isToday        = key === today
              const isSelected     = selectedKeys.has(key)
              const active         = dayEntries.filter(e => e.status !== 'canceled')
              const hasReceivable  = active.some(e => e.entry_type === 'receivable')
              const hasPayable     = active.some(e => e.entry_type === 'payable')
              const hasOverdue     = active.some(e => e.status === 'open' && e.due_date < today)
              const allPaid        = active.length > 0 && active.every(e => e.status === 'paid')

              const rangeStart = isRangeStart(key)
              const rangeEnd = isRangeEnd(key)

              return (
                <button
                  key={key}
                  data-datekey={key}
                  onMouseDown={() => {
                    // Skip synthetic mousedown fired by the browser after a touch event
                    if (touchHandledRef.current) { touchHandledRef.current = false; return }
                    isDraggingRef.current = true
                    dragAnchorRef.current = key
                    setSelectedKeys(new Set([key]))
                  }}
                  onMouseEnter={() => {
                    if (!isDraggingRef.current || !dragAnchorRef.current) return
                    setSelectedKeys(computeRange(dragAnchorRef.current, key, calendarDays))
                  }}
                    onTouchStart={(e) => {
                      const touch = e.touches?.[0]
                      if (!touch) return
                      
                      touchStartPosRef.current = { x: touch.clientX, y: touch.clientY }
                      
                      // Start timer to enable drag mode if user holds down
                      if (longPressTimerRef.current) {
                        window.clearTimeout(longPressTimerRef.current as number)
                      }
                      
                      isDragReadyRef.current = false
                      longPressTimerRef.current = window.setTimeout(() => {
                        isDragReadyRef.current = true
                        dragAnchorRef.current = key
                        longPressTimerRef.current = null
                      }, LONG_PRESS_MS)
                    }}
                    onTouchEnd={() => {
                      const wasDragging = isDraggingRef.current
                      
                      // Clean up timers and drag state
                      if (longPressTimerRef.current) {
                        window.clearTimeout(longPressTimerRef.current as number)
                        longPressTimerRef.current = null
                      }
                      isDragReadyRef.current = false
                      isDraggingRef.current = false
                      dragAnchorRef.current = null
                      touchStartPosRef.current = null
                      
                      // If it wasn't a drag, treat as a simple tap and select the date
                      if (!wasDragging) {
                        touchHandledRef.current = true
                        setSelectedKeys(new Set([key]))
                      }
                    }}
                    onTouchCancel={() => {
                      if (longPressTimerRef.current) {
                        window.clearTimeout(longPressTimerRef.current as number)
                        longPressTimerRef.current = null
                      }
                      isDragReadyRef.current = false
                      isDraggingRef.current = false
                      dragAnchorRef.current = null
                      touchStartPosRef.current = null
                    }}
                  className={`
                    relative h-14 flex flex-col items-center justify-start pt-1.5
                    border-b border-r border-slate-100 dark:border-slate-700/40
                    transition-colors focus:outline-none focus:ring-1 focus:ring-inset focus:ring-blue-400
                    select-none
                    ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-slate-50 dark:hover:bg-slate-700/30'}
                    ${rangeStart ? 'border-l-4 border-l-blue-600 dark:border-l-blue-400' : ''}
                    ${rangeEnd ? 'border-r-4 border-r-blue-600 dark:border-r-blue-400' : ''}
                    ${isSelected && selectedKeys.size === 1 ? 'border-2 border-blue-600 dark:border-blue-400' : ''}
                    ${isSelected && selectedKeys.size > 1 ? 'border-t-2 border-t-blue-600 dark:border-t-blue-400 border-b-2 border-b-blue-600 dark:border-b-blue-400' : ''}
                  `}
                >
                  <span className={`
                    w-7 h-7 flex items-center justify-center rounded-full text-sm font-medium leading-none
                    ${allPaid && isToday
                      ? 'bg-emerald-500 text-white line-through decoration-white decoration-2'
                      : allPaid
                      ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 line-through decoration-emerald-500 decoration-2'
                      : isToday
                      ? 'bg-blue-600 text-white'
                      : isSelected
                      ? 'text-blue-700 dark:text-blue-300 font-semibold'
                      : 'text-slate-700 dark:text-slate-300'
                    }
                  `}>
                    {day.getDate()}
                  </span>

                  {active.length > 0 && (
                    <div className="flex items-center gap-0.5 mt-0.5">
                      {allPaid
                        ? <span className="text-emerald-500 dark:text-emerald-400 text-[10px] leading-none">✓</span>
                        : <>
                            {hasReceivable && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
                            {hasPayable    && <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />}
                            {hasOverdue    && <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />}
                          </>
                      }
                    </div>
                  )}
                </button>
              )
            })}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-4 px-5 py-2.5 border-t border-slate-100 dark:border-slate-700">
            <span className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
              <span className="w-2 h-2 rounded-full bg-emerald-500" /> {db('calendar.legendReceivable')}
            </span>
            <span className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
              <span className="w-2 h-2 rounded-full bg-rose-500" /> {db('calendar.legendPayable')}
            </span>
            <span className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
              <span className="w-2 h-2 rounded-full bg-orange-500" /> {db('calendar.legendOverdue')}
            </span>
            <span className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
              <span className="w-4 h-4 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center text-emerald-600 dark:text-emerald-400 text-[9px] font-bold">✓</span> {db('calendar.legendAllPaid')}
            </span>
          </div>
        </div>

        {/* Day panel — right column on desktop, below on mobile */}
        <div>
          {selectedKeys.size > 0 ? (
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-visible">
              <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                <h2 className="font-semibold text-slate-900 dark:text-white capitalize text-sm">
                  {selectedKeys.size === 1
                    ? formatDateLabel([...selectedKeys][0])
                    : `${selectedKeys.size} dias selecionados (${getDateRangeLabel()})`}
                </h2>
                <button onClick={() => setSelectedKeys(new Set())} className="p-1 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Totals for the day (always visible) */}
              <div className="px-5 py-3 bg-slate-50 dark:bg-slate-900/30 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between gap-2">
                <div className="flex gap-6">
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{db('cards.receivable')}</p>
                    {(() => {
                      const total = selectedEntries.filter(e => e.entry_type === 'receivable').reduce((s, e) => s + e.amount, 0);
                      const open  = selectedEntries.filter(e => e.entry_type === 'receivable' && e.status === 'open').reduce((s, e) => s + e.amount, 0);
                      const hasPaid = selectedEntries.some(e => e.entry_type === 'receivable' && e.status === 'paid');
                      return hasPaid ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="line-through text-slate-400 dark:text-slate-500 text-sm font-semibold tabular-nums">{formatCurrency(total)}</span>
                          <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-300 tabular-nums">{formatCurrency(open)}</span>
                        </div>
                      ) : (
                        <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-300 tabular-nums">{formatCurrency(total)}</p>
                      );
                    })()}
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{db('cards.payable')}</p>
                    {(() => {
                      const total = selectedEntries.filter(e => e.entry_type === 'payable').reduce((s, e) => s + e.amount, 0);
                      const open  = selectedEntries.filter(e => e.entry_type === 'payable' && e.status === 'open').reduce((s, e) => s + e.amount, 0);
                      const hasPaid = selectedEntries.some(e => e.entry_type === 'payable' && e.status === 'paid');
                      return hasPaid ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="line-through text-slate-400 dark:text-slate-500 text-sm font-semibold tabular-nums">{formatCurrency(total)}</span>
                          <span className="text-sm font-semibold text-rose-600 dark:text-rose-300 tabular-nums">{formatCurrency(open)}</span>
                        </div>
                      ) : (
                        <p className="text-sm font-semibold text-rose-600 dark:text-rose-300 tabular-nums">{formatCurrency(total)}</p>
                      );
                    })()}
                  </div>
                </div>
                {/* Share and Download buttons */}
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={handleShareText}
                    title="Compartilhar como texto"
                    className="p-2 rounded-lg text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185z" />
                    </svg>
                  </button>
                  <button
                    onClick={handleDownloadPDF}
                    title="Baixar como PDF"
                    className="p-2 rounded-lg text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* List of entries or empty state */}
              {selectedEntries.length === 0 ? (
                <div className="py-10 text-center text-sm text-slate-400 dark:text-slate-500">
                  {db('dayPanel.empty')}
                </div>
              ) : (
                <ul className="divide-y divide-slate-100 dark:divide-slate-700 pb-48 sm:pb-0">
                    {selectedEntries.map(entry => (console.log('Dashboard entry debug:', { id: entry.id, party_id: entry.party_id, party_pix_key_snapshot: entry.party_pix_key_snapshot }),
                      <li key={entry.id} className="flex items-center justify-between gap-2 sm:gap-4 px-5 py-3.5 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <p className={`text-sm font-medium truncate ${entry.status === 'paid' ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-900 dark:text-white'}`}>
                            {entry.description}
                          </p>
                          {/* Desktop: party name below title */}
                          {entry.party_name_snapshot && (
                            <p className={`hidden sm:block text-xs mt-0.5 ${entry.status === 'paid' ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-500 dark:text-slate-400'}`}>
                              {entry.party_name_snapshot}
                            </p>
                          )}

                          {/* Desktop: pix key (if any) shown as a separate last line under party name */}
                          {entry.party_pix_key_snapshot && (
                            <div className="hidden sm:flex items-center gap-2 mt-0.5 text-xs">
                              <span className={`font-mono truncate max-w-[140px] ${entry.status === 'paid' ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-500 dark:text-slate-400'}`}>
                                {entry.party_pix_key_snapshot}
                              </span>
                              {entry.status !== 'paid' && (
                                <button
                                  onClick={() => handleCopyPix(entry.party_pix_key_snapshot!, entry.id)}
                                  title={db('share.tooltip')}
                                  className="flex-shrink-0 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                >
                                  {copiedPixIds.includes(entry.id) ? (
                                    <svg className="h-4 w-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  ) : (
                                    <svg className="h-4 w-4 text-slate-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                  )}
                                </button>
                              )}
                            </div>
                          )}

                          {/* Mobile: show party (if any), pix key on its own line, and amount (copyable) */}
                          <div className="sm:hidden flex flex-col gap-1 mt-0.5">
                            {entry.party_name_snapshot && (
                              <p className={`text-xs ${entry.status === 'paid' ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-500'}`}>
                                {entry.party_name_snapshot}
                              </p>
                            )}
                            {entry.party_pix_key_snapshot && (
                              <div className="flex items-center gap-2">
                                <span className={`font-mono truncate max-w-[160px] text-xs ${entry.status === 'paid' ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-500 dark:text-slate-400'}`}>
                                  {entry.party_pix_key_snapshot}
                                </span>
                                {entry.status !== 'paid' && (
                                  <button
                                    onClick={() => handleCopyPix(entry.party_pix_key_snapshot!, entry.id)}
                                    title={db('share.tooltip')}
                                    className="flex-shrink-0 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                  >
                                    {copiedPixIds.includes(entry.id) ? (
                                      <svg className="h-4 w-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                    ) : (
                                      <svg className="h-4 w-4 text-slate-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                      </svg>
                                    )}
                                  </button>
                                )}
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-semibold tabular-nums ${entry.status === 'paid' ? 'line-through text-slate-400 dark:text-slate-500' : (entry.entry_type === 'receivable' ? 'text-emerald-600' : 'text-rose-600')}`}>
                                ({formatCurrency(entry.amount)})
                              </span>
                              {entry.status !== 'paid' && (
                                <button
                                  onClick={() => handleCopyAmount(entry.amount, entry.id)}
                                  title="Copiar valor"
                                  className="flex-shrink-0 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                >
                                  {copiedAmountIds.includes(entry.id) ? (
                                    <svg className="h-4 w-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  ) : (
                                    <svg className="h-4 w-4 text-slate-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {entry.status !== 'paid' && (
                            <button
                              onClick={() => handleCopyAmount(entry.amount, entry.id)}
                              title="Copiar valor"
                              className="hidden sm:inline flex-shrink-0 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                            >
                              {copiedAmountIds.includes(entry.id) ? (
                                <svg className="h-4 w-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="h-4 w-4 text-slate-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              )}
                            </button>
                          )}
                          <span className={`hidden sm:inline text-sm font-bold tabular-nums ${entry.status === 'paid' ? 'line-through text-slate-400 dark:text-slate-500' : entry.entry_type === 'receivable' ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {formatCurrency(entry.amount)}
                          </span>
                          <StatusBadge entry={entry} />
                        </div>
                        {/* Actions menu dropdown - rightmost (extracted to EntryActions) */}
                        <div onClick={e => e.stopPropagation()} className="flex-shrink-0">
                          <EntryActions
                            entry={entry}
                            isMobile={isMobile}
                            isMember={isMember}
                            onActionDone={() => invalidateEntries()}

                          />
                        </div>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          ) : (
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 text-center">
              <p className="text-sm text-slate-400 dark:text-slate-500">
                {db('dayPanel.hint')}
              </p>
            </div>
          )}
        </div>

      </div>
    </div>

      {/* Mobile search popover (renders under header) */}
      {showSearchPopover && isMobile && (
        <div ref={searchHintRef} className="fixed top-20 left-0 right-0 px-4 z-50 pointer-events-auto">
          <div className="mx-auto max-w-screen-2xl bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden p-3">
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{t('actions.search')}</span>
                <button
                  onClick={() => setShowSearchPopover(false)}
                  className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:text-slate-200 dark:hover:bg-slate-700 transition-colors"
                  aria-label={t('actions.close')}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="relative">
                <input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') setShowSearchPopover(false) }}
                  placeholder={t('actions.search') + '...'}
                  autoFocus
                  className="w-full px-3 py-2.5 pr-9 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm font-medium"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                    aria-label="Limpar"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">{te('filters.searchHintShort')}</p>
            </div>
          </div>
        </div>
      )}

    {/* Receipt/Notify modals agora são gerenciados por `EntryActions`. */}
    {/* ── Mobile FAB ────────────────────────────────────────────────────── */}
    {!isMember && (
      <div className="sm:hidden fixed bottom-6 right-6 z-50 flex flex-col items-end pointer-events-none" ref={mobileRef}>
        {/* Speed-dial options */}
        <div className={`flex flex-col items-end gap-2 mb-3 transition-all duration-200 ${fabOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
          <button
            onClick={() => handleNewEntry('receivable')}
            className="flex items-center gap-2 pl-3 pr-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold shadow-lg transition-colors"
          >
            <span className="text-base leading-none">↑</span>
            {db('newReceivable')}
          </button>
          <button
            onClick={() => handleNewEntry('payable')}
            className="flex items-center gap-2 pl-3 pr-4 py-2 rounded-full bg-rose-500 hover:bg-rose-600 text-white text-sm font-semibold shadow-lg transition-colors"
          >
            <span className="text-base leading-none">↓</span>
            {db('newPayable')}
          </button>
        </div>

        {/* Main FAB button */}
        <button
          onClick={() => setFabOpen(o => !o)}
          className={`pointer-events-auto w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white shadow-xl flex items-center justify-center transition-all duration-200 ${fabOpen ? 'rotate-45' : ''}`}
        aria-label={db('newEntryAriaLabel')}
      >
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      </button>
    </div>
    )}


    {modalType && (
      <EntryModal
        isOpen={true}
        entryType={modalType}
        initialEntry={null}
        defaultDueDate={defaultDueDateForNewEntry}
        onClose={() => { setModalType(null); handleModalSuccess() }}
      />
    )}

    <ToastContainer />

    {/* Modais e dialogs relacionados a lançamentos agora são gerenciados por `EntryActions`. */}
    </>
  )
}
